// Based on confluence/packages/confluence-frontend-server/src/memory-monitor.js
import React, { useRef, useLayoutEffect, useEffect } from 'react';
import type AnalyticsClient from '@atlassiansox/analytics-web-client';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import { addRouteListener } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';

export const HEAP_THRESHOLD_TO_ALERT = 0.8;
export const CHECK_MEMORY_INTERVAL = 1000 * 10; // 10 seconds

export const EVENT_LISTENER_THRESHOLD = 4000; // issue view has ~2.5k event listeners
let routeName = 'unknown';
let analyticsClient: AnalyticsClient | null | undefined = null;
let memoryMonitorInterval: NodeJS.Timeout | null = null;
let _eventListenerCount = 0;
let initializedEventListeners = false;

function bytesToMB(bytes: number) {
	const MB = 1024 * 1024;
	return bytes / MB;
}

type PerformanceMemory = { jsHeapSizeLimit: number; usedJSHeapSize: number };
// exported for testing
export const getPerformanceMemory = (): PerformanceMemory | undefined => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage, no-restricted-properties, @typescript-eslint/consistent-type-assertions
	const performance = window?.performance as { memory?: PerformanceMemory };

	return performance?.memory;
};

export const getEventListenerCount = () => _eventListenerCount; // for testing

// exported for testing
export function checkMemoryUsage(client?: AnalyticsClient) {
	if (client) {
		analyticsClient = client;
	}
	if (!analyticsClient) {
		return false;
	}

	const memory = getPerformanceMemory();
	if (memory) {
		const { jsHeapSizeLimit, usedJSHeapSize } = memory;
		const heapThreshold = jsHeapSizeLimit * HEAP_THRESHOLD_TO_ALERT;
		if (usedJSHeapSize > heapThreshold) {
			analyticsClient.sendOperationalEvent({
				source: 'jira-memory-monitor',
				action: 'measured',
				actionSubject: 'highHeapUsage',
				attributes: {
					heapUsagePercent: Math.floor((usedJSHeapSize / jsHeapSizeLimit) * 100),
					heapUsageMB: Math.floor(bytesToMB(usedJSHeapSize)),
					maxHeapSizeMB: Math.floor(bytesToMB(jsHeapSizeLimit)),
					route: routeName,
				},
			});
		}
	}
	if (initializedEventListeners && getEventListenerCount() > EVENT_LISTENER_THRESHOLD) {
		analyticsClient.sendOperationalEvent({
			source: 'jira-memory-monitor',
			action: 'measured',
			actionSubject: 'highEventListenerCount',
			attributes: {
				eventListenerCount: getEventListenerCount(),
				route: routeName,
			},
		});
	}
	return true;
}

export function addCounterToEventListener() {
	const originalAddEventListener = EventTarget.prototype.addEventListener;
	const originalRemoveEventListener = EventTarget.prototype.removeEventListener;

	if (!initializedEventListeners) {
		EventTarget.prototype.addEventListener = function addEventListenerWithCounter(
			type,
			listener,
			options,
		) {
			_eventListenerCount++;
			originalAddEventListener.call(this, type, listener, options);
		};

		EventTarget.prototype.removeEventListener = function removeEventListenerWithCounter(
			type,
			listener,
			options,
		) {
			_eventListenerCount--;
			originalRemoveEventListener.call(this, type, listener, options);
		};
		initializedEventListeners = true;
	}
}

export function initializeMemoryMonitor(client?: AnalyticsClient) {
	analyticsClient = client;
	addCounterToEventListener();
	if (!memoryMonitorInterval) {
		memoryMonitorInterval = setInterval(checkMemoryUsage, CHECK_MEMORY_INTERVAL);
	}
	return true;
}

export function setMemoryMonitorRouteName(name: string) {
	routeName = name;
}

export function clearMemoryMonitorInterval() {
	if (memoryMonitorInterval) {
		clearInterval(memoryMonitorInterval);
		memoryMonitorInterval = null;
	}
}

const MemoryMonitorClient = () => {
	const hasInit = useRef(false);
	useLayoutEffect(() => {
		if (hasInit.current) {
			return;
		}
		hasInit.current = true;
		getAnalyticsWebClientPromise().then((client) => {
			const instance = client?.getInstance();
			if (instance) {
				analyticsClient = instance;
				initializeMemoryMonitor(instance);
			}
		});
		return clearMemoryMonitorInterval;
	});

	useEffect(() => {
		const unsubscribe = addRouteListener((state) => {
			setMemoryMonitorRouteName(state?.route?.name ?? 'unknown');
		});
		return unsubscribe;
	}, []);

	return null;
};

export const MemoryMonitor = () => {
	if (__SERVER__) {
		return null;
	}
	return <MemoryMonitorClient />;
};
